import jsPDF from "jspdf";
import API from "../services/api/axios";
import { getBothCourtCasesDetailsByCourtRef } from "../services/api/CasesApi";
import { getUserData } from "../services/api/AuthApi";
import { getBranchByBranchID } from "../services/api/BranchApi";
import { highCourtMap } from "../constants/CourtNames";
import logoImage from "../assets/images/dark-main-pro.png";
import { normalizePartyNames } from "./TruncateParties";
import drawCommonPdfHeader from "./CommonPdfHeader/CommonPdfHeader";

export const fetchUserName = async (userId) => {
  try {
    const res = await getUserData(userId);
    return res.data?.name || res.data?.username || "Unknown User";
  } catch (err) {
    console.error("Error fetching user data:", err);
    return "Unknown User";
  }
};

export const fetchBranchName = async (branchId) => {
  try {
    const response = await getBranchByBranchID(branchId);
    return response.data?.name || response.data?.branchName || "Unknown Branch";
  } catch (error) {
    console.error("Error fetching branch name:", error);
    return "Unknown Branch";
  }
};

const getHighCourtName = (courtId) => {
  const id = parseInt(courtId);
  return highCourtMap[id] || highCourtMap[courtId] || "Unknown Court";
};

export const getLogoBase64 = async () => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        
        // Reduce logo size
        const targetWidth = 125;
        const targetHeight = 35;
        
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        
        // JPEG with 60% quality instead of PNG
        const dataURL = canvas.toDataURL("image/jpeg", 0.6);
        resolve(dataURL);
      } catch (error) {
        console.error("Error converting logo to base64:", error);
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = logoImage;
  });
};

// Helper function to extract additional column data
const getAdditionalColumnData = (hearing, columnId) => {
  const caseData = hearing.caseDate || hearing;

  // Check if this is a custom column
  const isCustomColumn = columnId.startsWith('custom_');
  if (isCustomColumn) {
    const customFieldName = columnId.replace('custom_', '');
    const customAttributes = caseData.customAttributes;
    if (customAttributes?.columns && Array.isArray(customAttributes.columns)) {
      const customCol = customAttributes.columns.find(col => col.name === customFieldName);
      if (customCol) {
        return {
          value: customCol.value || null,
          type: "customColumn",
          label: customFieldName.charAt(0).toUpperCase() + customFieldName.slice(1).replace(/([A-Z])/g, ' $1')
        };
      }
    }
    return {
      value: null,
      type: "customColumn",
      label: customFieldName.charAt(0).toUpperCase() + customFieldName.slice(1).replace(/([A-Z])/g, ' $1')
    };
  }

  switch (columnId) {
    case "advocates":
      return {
        value: caseData.advocates || null,
        type: "advocates",
      };

    case "coram":
      return {
        value:
          caseData.caseStatus?.["Coram"] ||
          caseData.caseStatus?.["Court Number and Judge"] ||
          null,
        type: "coram",
      };

    case "caseStatus":
      if (caseData.caseStatus) {
        const status = caseData.caseStatus;
        return {
          stageOfCase: status["Stage of Case"] || status["Case Stage"] || null,
          benchType: status["Bench Type"] || null,
          judicialBranch: status["Judicial Branch"] || null,
          type: "caseStatus",
        };
      }
      return {
        stageOfCase: null,
        benchType: null,
        judicialBranch: null,
        type: "caseStatus",
      };

    case "caseDetails":
      if (caseData.caseDetails) {
        const details = caseData.caseDetails;
        return {
          filingDate: details["Filing Date"] || null,
          regDate: details["Registration Date"] || null,
          regNumber: details["Registration Number"] || null,
          type: "caseDetails",
        };
      }
      return {
        filingDate: null,
        regDate: null,
        regNumber: null,
        type: "caseDetails",
      };

    case "categoryDetails":
      if (caseData.categoryDetails) {
        const category = caseData.categoryDetails;
        return {
          category: category["Category"] || category.Category || null,
          type: "categoryDetails",
        };
      }
      return { category: null, type: "categoryDetails" };

    case "actsData":
      // if (caseData.actsData && Array.isArray(caseData.actsData)) {
      //   return {
      //     acts: caseData.actsData || caseData.actsAndSections,
      //     type: "actsData",
      //   };
      // }
      // return { acts: null, type: "actsData" };

      const actsSource = caseData.actsData || caseData.actsAndSections;

      if (actsSource && Array.isArray(actsSource)) {
        return {
          acts: actsSource,
          type: "actsData",
        };
      }
      return { acts: null, type: "actsData" };

    default:
      return "N/A";
  }
};

// Column labels for additional columns
const additionalColumnLabels = {
  advocates: "Advocates",
  coram: "Coram",
  caseStatus: "Case Status",
  caseDetails: "Case Details",
  categoryDetails: "Category Details",
  actsData: "Acts Data",
};

export const exportDashboardToPDF = async (
  dashboardData,
  orientation = "portrait",
  logoImageData = logoImage,
  additionalColumns = [],
  exportDateRange = null,
  globalSettings = null
) => {
 const doc = new jsPDF({
  orientation: orientation,
  unit: "pt",
  format: "a4",
  compress: true, // Add this line
});
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 46;
  const contentWidth = pageWidth - margin * 2;

  // Exact colors from the PDF
  const colors = {
    main: [0, 74, 154],
    blue: [0, 123, 255],
    white: [255, 255, 255],
    black: [33, 37, 41],
    gray: [108, 117, 125],
    lightGray: [233, 236, 239],
    borderGray: [222, 226, 230],
    green: [40, 167, 69],
    red: [220, 53, 69],
    pendingBg: [248, 215, 218],
    pendingText: [114, 28, 36],
    purple: [142, 68, 173],
    orange: [230, 126, 34],
    teal: [26, 188, 156],
    darkBlue: [52, 73, 94],
  };

  // Determine if Custom Columns should be shown based on Global Settings
  let customColumnsEnabled = false;
  let customColumnNames = [];
  if (globalSettings) {
    const targetSettings = globalSettings.result?.[0];
    if (targetSettings?.cases?.allowCustomColumns) {
      customColumnsEnabled = true;
      customColumnNames = targetSettings.cases.columns || [];
    }
  }

  let currentY = 0;
  let pageNumber = 1;

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return "Not scheduled";
    try {
      let date;
      if (
        dateString.includes("th ") ||
        dateString.includes("st ") ||
        dateString.includes("nd ") ||
        dateString.includes("rd ")
      ) {
        let cleanDate = dateString.replace(/(\d+)(th|st|nd|rd)/, "$1");
        date = new Date(cleanDate);
      } else if (dateString.includes("T")) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return "TBD";

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      });
    } catch (error) {
      console.error("Date formatting error:", error, dateString);
      return "TBD";
    }
  };

  const formatLongDate = (dateString) => {
    if (!dateString) return "";
    try {
      let date;
      if (
        dateString.includes("th ") ||
        dateString.includes("st ") ||
        dateString.includes("nd ") ||
        dateString.includes("rd ")
      ) {
        let cleanDate = dateString.replace(/(\d+)(th|st|nd|rd)/, "$1");
        date = new Date(cleanDate);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        return new Date().toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      }

      return date.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (error) {
      return new Date().toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
  };

  const getCompanyName = () => {
    try {
      const globalSettings = localStorage.getItem("globalSettings");
      const company = localStorage.getItem("company");
      const userData = localStorage.getItem("userData");
      const userProfile = localStorage.getItem("userProfile");

      if (globalSettings) {
        const settings = JSON.parse(globalSettings);
        if (settings.companyName) return settings.companyName;
      }

      if (company) {
        const companyData = JSON.parse(company);
        if (companyData.companyName) return companyData.companyName;
        if (companyData.name) return companyData.name;
      }

      if (userData) {
        const user = JSON.parse(userData);
        if (user.companyName) return user.companyName;
        if (user.company && user.company.name) return user.company.name;
      }

      if (userProfile) {
        const profile = JSON.parse(userProfile);
        if (profile.companyName) return profile.companyName;
        if (profile.company) return profile.company;
      }

      return "LawSuit(Pro)";
    } catch (error) {
      console.error("Error retrieving company name:", error);
      return "LawSuit(Pro)";
    }
  };

  // Draw professional calendar icon for hearings
  const drawHearingIcon = (x, y, size = 14) => {
    doc.setFillColor(...colors.white);
    doc.setDrawColor(...colors.white);
    doc.setLineWidth(1.2);

    doc.roundedRect(x + 1, y + 3, size - 2, size - 4, 1, 1, "S");
    doc.setFillColor(...colors.white);
    doc.rect(x + 1, y + 3, size - 2, 3, "F");
    doc.circle(x + 4, y + 2, 1, "F");
    doc.circle(x + size - 4, y + 2, 1, "F");
    doc.setLineWidth(0.8);
    doc.line(x + 2, y + 8, x + size - 2, y + 8);
    doc.line(x + 2, y + 11, x + size - 2, y + 11);
    doc.line(x + 5, y + 6, x + 5, y + size - 1);
    doc.line(x + 9, y + 6, x + 9, y + size - 1);
  };

  const drawTaskIcon = (x, y, size = 14) => {
    doc.setDrawColor(...colors.white);
    doc.setLineWidth(2.5);
    doc.setLineCap("round");
    doc.setLineJoin("round");

    doc.line(x + 3, y + size / 2, x + size / 2 - 0.5, y + size - 3);
    doc.line(x + size / 2 - 0.5, y + size - 3, x + size - 2, y + 3);
    doc.setLineWidth(1.2);
    doc.circle(x + size / 2, y + size / 2 + 1, size / 2 - 1, "S");
  };

  const addHeader = (scheduleDate, totalHearings, totalTasks) => {
    const companyName = getCompanyName();
    const totalItems = totalHearings + totalTasks;

    doc.setFillColor(...colors.main);
    doc.rect(0, 0, pageWidth, 100, "F");

    if (logoImageData) {
      try {
        const logoX = margin;
        const logoY = 20;
        const logoWidth = 125;
        const logoHeight = 35;

        doc.addImage(logoImageData, "PNG", logoX, logoY, logoWidth, logoHeight);
      } catch (error) {
        console.error("Error adding logo to PDF:", error);
      }
    }

    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.white);
    if (logoImageData) {
      doc.text(companyName, pageWidth - margin, 42, { align: "right" });
    } else {
      doc.text(companyName, pageWidth / 2, 42, { align: "center" });
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    if (logoImageData) {
      doc.text("Daily Dashboard Report", pageWidth - margin, 70, {
        align: "right",
      });
    } else {
      doc.text("Daily Dashboard Report", pageWidth / 2, 70, {
        align: "center",
      });
    }

    currentY = 140;

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.black);
    const itemsText = `Total Items: ${totalItems} (${totalHearings} Hearings, ${totalTasks} Tasks)`;
    doc.text(itemsText, pageWidth / 2, currentY, { align: "center" });

    currentY += 22;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.gray);
    doc.text(scheduleDate, pageWidth / 2, currentY, { align: "center" });

    currentY += 35;
  };

  const addCleanSectionHeader = (title, isTask = false) => {
    const headerColor = isTask ? colors.green : colors.blue;

    // ✅ CRITICAL FIX: Calculate exact space needed for header + table header + at least 2 rows
    const headerHeight = 28;
    const tableHeaderHeight = 28;
    const minRowHeight = 40; // Minimum height for at least one complete row
    const minContentHeight = tableHeaderHeight + minRowHeight * 2; // Ensure 2 rows fit
    const minSpaceNeeded = headerHeight + minContentHeight; // Total: 28 + 28 + 80 = 136pt

    // ✅ Check if there's enough space for header + meaningful content
    if (currentY + minSpaceNeeded > pageHeight - 80) {
      // Not enough space - move to next page
      addFooter();
      doc.addPage();
      pageNumber++;
      currentY = margin + 20;
    }

    // Draw section header
    doc.setFillColor(...headerColor);
    doc.rect(margin, currentY, contentWidth, headerHeight, "F");

    const iconX = margin + 10;
    const iconY = currentY + 8;

    if (isTask) {
      drawTaskIcon(iconX, iconY, 12);
    } else {
      drawHearingIcon(iconX, iconY, 12);
    }

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.white);
    doc.text(title, margin + 30, currentY + 18);

    currentY += headerHeight; // Move down by exactly header height (28pt)
  };

  const calculateLineCount = (data, colWidth) => {
    let lineCount = 2;

    if (data && typeof data === "object" && data.type) {
      // --- NEW: Advocates Only ---
      if (data.type === "advocates") {
        if (data.value) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);

          const lines = doc.splitTextToSize(String(data.value), colWidth);
          lineCount = lines.length;
        } else {
          lineCount = 1;
        }
      }

      // --- NEW: Coram Only ---
      else if (data.type === "coram") {
        if (data.value) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);

          const lines = doc.splitTextToSize(String(data.value), colWidth);
          lineCount = lines.length;
        } else {
          lineCount = 1;
        }
      }

      // --- EXISTING: Case Status ---
      else if (data.type === "caseStatus") {
        lineCount = 0;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);

        if (data.stageOfCase) {
          lineCount += 1; // Label
          const stageLines = doc.splitTextToSize(data.stageOfCase, colWidth);
          lineCount += stageLines.length;
        }
        if (data.benchType) {
          lineCount += 1; // Label
          lineCount += 1; // Value
        }
        if (data.judicialBranch) {
          lineCount += 1; // Label
          lineCount += 1; // Value
        }
        lineCount = Math.max(lineCount, 1);
      }

      // --- EXISTING: Case Details ---
      else if (data.type === "caseDetails") {
        lineCount = 0;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);

        if (data.filingDate) {
          lineCount += 1; // Label
          lineCount += 1; // Value
        }
        if (data.regDate) {
          lineCount += 1; // Label
          lineCount += 1; // Value
        }
        if (data.regNumber) {
          lineCount += 1; // Label
          const regLines = doc.splitTextToSize(
            String(data.regNumber),
            colWidth
          );
          lineCount += regLines.length;
        }
        lineCount = Math.max(lineCount, 1);
      }

      // --- EXISTING: Category Details ---
      else if (data.type === "categoryDetails") {
        if (data.category) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          const categoryLines = doc.splitTextToSize(data.category, colWidth);
          lineCount = categoryLines.length;
        } else {
          lineCount = 1;
        }
      }

      // --- FIXED: Acts Data ---
      else if (data.type === "actsData") {
        if (data.acts && data.acts.length > 0) {
          lineCount = 0;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);

          data.acts.forEach((act, idx) => {
            const actName = act.underAct || act.act;
            const sectionValue = act.underSection || act.section;
            if (actName) {
              const actLines = doc.splitTextToSize(String(actName), colWidth);
              // Each act line uses 9pt spacing = 0.75 lines (9/12)
              lineCount += actLines.length * 0.75;
            }
            if (sectionValue) {
              const sectionLines = doc.splitTextToSize(
                `Section: ${sectionValue}`,
                colWidth
              );
              // Section lines also use 9pt spacing = 0.75 lines (9/12)
              lineCount += sectionLines.length * 0.75;
            }
            // Add spacing between acts (only if not the last act)
            if (idx < data.acts.length - 1) {
              lineCount += 0.25; // 3pt spacing = 0.25 lines (3/12)
            }
          });
          // Ensure minimum line count
          lineCount = Math.max(lineCount, 1);
        } else {
          lineCount = 1;
        }
      }
    }

    // --- EXISTING: Plain String Data ---
    else if (typeof data === "string") {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      const lines = data.split("\n");
      const wrappedLines = lines.reduce((total, line) => {
        const wrapped = doc.splitTextToSize(line, colWidth);
        return total + wrapped.length;
      }, 0);
      lineCount = wrappedLines;
    }

    return lineCount;
  };

  const renderAdditionalColumnData = (data, columnId, x, y, colWidth, maxY) => {
    if (data && typeof data === "object" && data.type) {
      // --- RENDER ADVOCATES ---
      if (data.type === "advocates") {
        if (data.value) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(...colors.orange);
          const lines = doc.splitTextToSize(String(data.value), colWidth);
          lines.forEach((line) => {
            if (y < maxY) {
              doc.text(line, x, y);
              y += 10;
            }
          });
          y += 2;
        } else {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("N/A", x, y);
          y += 10;
          y += 2;
        }
      }

      // --- RENDER CORAM ---
      else if (data.type === "coram") {
        if (data.value) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(...colors.red);
          const lines = doc.splitTextToSize(String(data.value), colWidth);
          lines.forEach((line) => {
            if (y < maxY) {
              doc.text(line, x, y);
              y += 10;
            }
          });
          y += 2;
        } else {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("N/A", x, y);
          y += 10;
          y += 2;
        }
      }

      // --- RENDER CASE STATUS ---
      else if (data.type === "caseStatus") {
        const hasAnyStatus =
          data.stageOfCase || data.benchType || data.judicialBranch;

        if (hasAnyStatus) {
          if (data.stageOfCase && y < maxY) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Stage of Case:", x, y);
            y += 10;
            y += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(...colors.blue);
            const stageLines = doc.splitTextToSize(
              String(data.stageOfCase),
              colWidth
            );
            stageLines.forEach((line) => {
              if (y < maxY) {
                doc.text(line, x, y);
                y += 10;
              }
            });
            y += 2;
          }

          if (data.benchType && y < maxY) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Bench Type:", x, y);
            y += 10;
            y += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(...colors.purple);
            doc.text(String(data.benchType), x, y);
            y += 10;
            y += 2;
          }

          if (data.judicialBranch && y < maxY) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Judicial Branch:", x, y);
            y += 10;
            y += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(...colors.teal);
            doc.text(String(data.judicialBranch), x, y);
            y += 10;
            y += 2;
          }
        } else {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("N/A", x, y);
          y += 10;
          y += 2;
        }
      }

      // --- RENDER CASE DETAILS ---
      else if (data.type === "caseDetails") {
        if (data.filingDate && y < maxY) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("Filing Date:", x, y);
          y += 10;
          y += 1;

          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(41, 128, 185);
          doc.text(String(data.filingDate), x, y);
          y += 10;
          y += 2;
        }

        if (data.regDate && y < maxY) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("Reg. Date:", x, y);
          y += 10;
          y += 1;

          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(...colors.purple);
          doc.text(String(data.regDate), x, y);
          y += 10;
          y += 2;
        }

        if (data.regNumber && y < maxY) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("Reg. Number:", x, y);
          y += 10;
          y += 1;

          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(...colors.green);
          const regLines = doc.splitTextToSize(
            String(data.regNumber),
            colWidth
          );
          regLines.forEach((line) => {
            if (y < maxY) {
              doc.text(line, x, y);
              y += 10;
            }
          });
          y += 2;
        }

        if (!data.filingDate && !data.regDate && !data.regNumber) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("N/A", x, y);
          y += 10;
          y += 2;
        }
      }

      // --- RENDER CATEGORY DETAILS ---
      else if (data.type === "categoryDetails") {
        if (data.category) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(...colors.teal);
          const categoryLines = doc.splitTextToSize(
            String(data.category),
            colWidth
          );
          categoryLines.forEach((line) => {
            if (y < maxY) {
              doc.text(line, x, y);
              y += 10;
            }
          });
          y += 2;
        } else {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("N/A", x, y);
          y += 10;
          y += 2;
        }
      }

      // --- RENDER ACTS DATA ---
      else if (data.type === "actsData") {
        if (data.acts && data.acts.length > 0) {
          data.acts.forEach((act, actIdx) => {
            if (y < maxY) {
              const actName = act.underAct || act.act;
              const sectionValue = act.underSection || act.section;
              if (actName) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(7);
                doc.setTextColor(...colors.darkBlue);
                const actLines = doc.splitTextToSize(String(actName), colWidth);
                actLines.forEach((line) => {
                  if (y < maxY) {
                    doc.text(line, x, y);
                    y += 9;
                  }
                });
                y += 2;
              }

              if (sectionValue) {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(7);
                doc.setTextColor(...colors.gray);
                const sectionText = `Section: ${sectionValue}`;
                const sectionLines = doc.splitTextToSize(sectionText, colWidth);
                sectionLines.forEach((line) => {
                  if (y < maxY) {
                    doc.text(line, x, y);
                    y += 9;
                    y += 2;
                  }
                });
              }

              if (actIdx < data.acts.length - 1) {
                y += 3;
              }
            }
          });
        } else {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("N/A", x, y);
          y += 10;
          y += 2;
        }
      }

      // --- RENDER CUSTOM COLUMNS ---
      else if (data.type === "customColumn") {
        if (data.value && String(data.value).trim() !== "") {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7);
          doc.setTextColor(...colors.blue);
          const lines = doc.splitTextToSize(String(data.value), colWidth);
          lines.forEach((line) => {
            if (y < maxY) {
              doc.text(line, x, y);
              y += 10;
            }
          });
          y += 2;
        } else {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7);
          doc.setTextColor(...colors.gray);
          doc.text("N/A", x, y);
          y += 10;
          y += 2;
        }
      }
    } else {
      // Fallback for plain string data
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...colors.black);
      const lines = doc.splitTextToSize(String(data), colWidth);
      lines.forEach((line) => {
        if (y < maxY) {
          doc.text(line, x, y);
          y += 10;
        }
      });
      y += 2;
    }

    return y;
  };

  // Helper function to format date for section headers
  const formatDateForHeader = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      // Get ordinal suffix for day
      const day = date.getDate();
      const getOrdinalSuffix = (d) => {
        if (d > 3 && d < 21) return "th";
        switch (d % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };

      const weekday = date.toLocaleDateString("en-GB", { weekday: "long" });
      const month = date.toLocaleDateString("en-GB", { month: "long" });
      const year = date.getFullYear();

      return `${weekday}, ${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Check if a date is today
  const isToday = (dateString) => {
    if (!dateString) return false;
    const today = new Date().toISOString().split("T")[0];
    const checkDate = new Date(dateString).toISOString().split("T")[0];
    return today === checkDate;
  };

  const addHearingsTable = async (hearingsList, groupedHearingsData = null) => {
    // Determine mode
    const isPortraitMode = orientation === "portrait";
    const shouldSplitLandscape =
      orientation === "landscape" && additionalColumns.length > 2;

    let colWidths;
    let columnsToShow = [];
    let stackedColumns = [];
    
    const customCols = customColumnsEnabled
      ? customColumnNames
          .map((col) => `custom_${col.name}`)
          .filter((colId) => additionalColumns.includes(colId)) // <--- CRITICAL FIX: Only include what user selected
          .filter((colId) => {
            // Check if any hearing has a value for this specific custom column
            return hearingsList.some((hearing) => {
              const caseData = hearing.caseDate || hearing;
              const customAttrs = caseData.customAttributes?.columns;
              if (customAttrs && Array.isArray(customAttrs)) {
                const customCol = customAttrs.find(
                  (c) => c.name === colId.replace("custom_", "")
                );
                return (
                  customCol &&
                  customCol.value &&
                  String(customCol.value).trim() !== ""
                );
              }
              return false;
            });
          })
      : [];
    
    const regularAdditionalCols = additionalColumns.filter(col => !col.startsWith('custom_'));

    if (isPortraitMode && (regularAdditionalCols.length > 0 || customCols.length > 0)) {
      // Filter out caseStatus, caseDetails, and coram from stackedColumns
      // since they appear in case title column (coram is now compulsory)
      const filteredStackedColumns = regularAdditionalCols.filter(
        (col) =>
          col !== "caseStatus" && col !== "caseDetails" && col !== "coram"
      );

      // Add custom columns to stacked columns in portrait mode
      const allStackedColumns = [...filteredStackedColumns, ...customCols];

      // Only show "Additional" column if there are columns to show
      if (allStackedColumns.length > 0) {
        colWidths = [45, 85, 110, 180, 80];
        columnsToShow = [];
        stackedColumns = allStackedColumns;
      } else {
        // No additional column needed - only show base 4 columns
        colWidths = [45, 100, 115, 240];
        columnsToShow = [];
        stackedColumns = [];
      }
    } else if (orientation === "landscape") {
      // LANDSCAPE MODE: Always use "Additional" column for custom columns
      // Filter out coram and custom columns from regular columns
      const filteredRegularColumns = regularAdditionalCols.filter(
        (col) => col !== "coram"
      );
      
      // If we have custom columns, add an "Additional" column
      if (customCols.length > 0) {
        if (filteredRegularColumns.length === 0) {
          // Only custom columns - simpler layout with narrower Additional column
          colWidths = [50, 110, 180, 230, 90];
          columnsToShow = [];
          stackedColumns = customCols;
        } else if (filteredRegularColumns.length <= 2) {
          // Few regular columns + custom columns
          const additionalColWidth = 90; // Fixed narrow width for Additional column
          const availableWidth = contentWidth - 50 - 110 - additionalColWidth;
          const remainingCols = 2 + filteredRegularColumns.length;
          const colWidth = Math.floor(availableWidth / remainingCols);
          colWidths = [50, 110, colWidth, colWidth];
          filteredRegularColumns.forEach(() => {
            colWidths.push(colWidth);
          });
          colWidths.push(additionalColWidth); // Narrower Additional column
          columnsToShow = filteredRegularColumns;
          stackedColumns = customCols;
        } else {
          // Many regular columns + custom columns
          const additionalColWidth = 85; // Fixed narrow width for Additional column
          const availableWidth = contentWidth - 50 - 90 - additionalColWidth;
          const remainingCols = 2 + filteredRegularColumns.length;
          const colWidth = Math.floor(availableWidth / remainingCols);
          colWidths = [50, 90, colWidth, colWidth];
          filteredRegularColumns.forEach(() => {
            colWidths.push(colWidth);
          });
          colWidths.push(additionalColWidth); // Narrower Additional column
          columnsToShow = filteredRegularColumns;
          stackedColumns = customCols;
        }
      } else if (shouldSplitLandscape) {
        // No custom columns, many regular columns
        const availableWidth = contentWidth - 50 - 90;
        const remainingCols = 2 + filteredRegularColumns.length;
        const colWidth = Math.floor(availableWidth / remainingCols);
        colWidths = [50, 90, colWidth, colWidth];
        filteredRegularColumns.forEach(() => {
          colWidths.push(colWidth);
        });
        columnsToShow = filteredRegularColumns;
      } else if (regularAdditionalCols.length === 0) {
        // No additional columns at all
        colWidths = [45, 100, 115, 240];
        columnsToShow = [];
      } else {
        // Few regular columns, no custom columns
        const availableWidth = contentWidth - 50 - 110;
        const remainingCols = 2 + filteredRegularColumns.length;
        const colWidth = Math.floor(availableWidth / remainingCols);
        colWidths = [50, 110, colWidth, colWidth];
        filteredRegularColumns.forEach(() => {
          colWidths.push(colWidth);
        });
        columnsToShow = filteredRegularColumns;
      }
    } else {
      // Fallback for any other case - default 4 columns
      colWidths = [45, 100, 115, 240];
      columnsToShow = [];
      stackedColumns = [];
    }

    // Helper function to draw table header
    const drawTableHeader = () => {
      const headerHeight = 28;
      doc.setFillColor(...colors.lightGray);
      doc.rect(margin, currentY, contentWidth, headerHeight, "F");

      doc.setDrawColor(...colors.borderGray);
      doc.setLineWidth(0.5);
      doc.rect(margin, currentY, contentWidth, headerHeight, "S");

      doc.setFontSize(columnsToShow.length > 2 ? 9 : 10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...colors.black);

      let xPos = margin + 8;

      // Always show base columns
      doc.text("SL No", xPos, currentY + 18);
      xPos += colWidths[0];
      doc.text("CR No", xPos, currentY + 18);
      xPos += colWidths[1];
      doc.text("Court Name", xPos, currentY + 18);
      xPos += colWidths[2];
      doc.text("Case Info", xPos, currentY + 18);
      xPos += colWidths[3];

      if (isPortraitMode && stackedColumns.length > 0) {
        // Portrait mode with stacked columns
        doc.text("Additional", xPos, currentY + 18);
      } else if (columnsToShow.length > 0 || stackedColumns.length > 0) {
        // Landscape mode with columns
        columnsToShow.forEach((colId, idx) => {
          let label = additionalColumnLabels[colId];
          
          // Format custom column labels (remove 'custom_' prefix and format nicely)
          if (!label && colId.startsWith('custom_')) {
            const fieldName = colId.replace('custom_', '');
            label = fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1');
          }
          
          doc.text(label || colId, xPos, currentY + 18);
          xPos += colWidths[4 + idx];
        });
        
        // Add "Additional" column header in landscape if there are stacked columns (custom columns)
        if (!isPortraitMode && stackedColumns.length > 0) {
          doc.text("Additional", xPos, currentY + 18);
        }
      }

      // Vertical lines
      xPos = margin + colWidths[0];
      doc.setDrawColor(...colors.borderGray);
      doc.setLineWidth(0.5);
      for (let i = 0; i < colWidths.length - 1; i++) {
        doc.line(xPos, currentY, xPos, currentY + headerHeight);
        xPos += colWidths[i + 1];
      }

      currentY += headerHeight;
    };

    // Check if we have grouped data (multiple dates)
    if (groupedHearingsData && Object.keys(groupedHearingsData).length > 1) {
      // Multiple dates - group by date
      const sortedDates = Object.keys(groupedHearingsData).sort(
        (a, b) => new Date(a) - new Date(b)
      );

      let globalSerialNumber = 1;

      for (const dateKey of sortedDates) {
        const dateHearings = groupedHearingsData[dateKey];
        if (!dateHearings || dateHearings.length === 0) continue;

        // Determine section header title
        let sectionTitle;
        if (isToday(dateKey)) {
          sectionTitle = `Today's Hearings`;
        } else {
          sectionTitle = `Hearings for ${formatDateForHeader(dateKey)} (${
            dateHearings.length
          })`;
        }

        addCleanSectionHeader(sectionTitle);

        // Draw table header for this date group
        drawTableHeader();

        // Add hearing rows for this date
        for (const hearing of dateHearings) {
          await addHearingRow(
            hearing,
            colWidths,
            globalSerialNumber,
            drawTableHeader,
            columnsToShow,
            stackedColumns,
            isPortraitMode
          );
          globalSerialNumber++;
        }

        // Add spacing between date groups
        const isLastDate = dateKey === sortedDates[sortedDates.length - 1];
        if (!isLastDate) {
          currentY += 12; //  CHANGED: Add spacing between groups
        }
      }
    } else {
      // Single date or no grouped data - use original behavior
      // Determine if it's today
      let sectionTitle;
      if (groupedHearingsData) {
        const dateKeys = Object.keys(groupedHearingsData);
        if (dateKeys.length === 1 && isToday(dateKeys[0])) {
          sectionTitle = `Today's Hearings`;
        } else if (dateKeys.length === 1) {
          sectionTitle = `Hearings for ${formatDateForHeader(dateKeys[0])}`;
        } else {
          sectionTitle = `Today's Hearings`;
        }
      } else {
        sectionTitle = `Today's Hearings`;
      }

      addCleanSectionHeader(sectionTitle);

      // Draw initial table header
      drawTableHeader();

      // Add hearing rows
      let serialNumber = 1;
      for (const hearing of hearingsList) {
        await addHearingRow(
          hearing,
          colWidths,
          serialNumber,
          drawTableHeader,
          columnsToShow,
          stackedColumns,
          isPortraitMode
        );
        serialNumber++;
      }
    }
  };

  const addHearingRow = async (
    hearing,
    colWidths,
    serialNumber,
    drawTableHeader,
    columnsToShow = [],
    stackedColumns = [],
    isPortraitMode = false
  ) => {
    const caseData = hearing.caseDate || hearing;

    const courtName = caseData.courtName || "N/A";
    const crNo = caseData.caseTypeAndNumber || "N/A";
    const slNo = serialNumber.toString();

    let caseTitle = "";
    if (caseData.parties) {
      caseTitle = normalizePartyNames(caseData.parties);
    }

    const cnrNo = caseData.cnrNumber || hearing.courtRef || "";

    // Get Next Hearing Date - check multiple possible sources (COMPULSORY)
    const nextHearing =
      caseData.customUndatedRows?.["Next Hearing Date"] ||
      caseData.caseStatus?.["Next Hearing Date"] ||
      "";
    // Track if next hearing is from customUndatedRows for color coding
    const isCustomNextHearing =
      !!caseData.customUndatedRows?.["Next Hearing Date"];

    // Get Coram data - ALWAYS displayed in case title column (compulsory)
    const coramData = getAdditionalColumnData(hearing, "coram");
    const coramValue = coramData?.value || null;

    // Get case status and case details data (will be shown in case title column ONLY IN PORTRAIT MODE)
    const caseStatusData = getAdditionalColumnData(hearing, "caseStatus");
    const caseDetailsData = getAdditionalColumnData(hearing, "caseDetails");

    // Get column data for regular columns (landscape)
    const regularColumnsData = columnsToShow.map((colId) =>
      getAdditionalColumnData(hearing, colId)
    );

    // Get stacked column data (portrait)
    const stackedColumnsData = stackedColumns.map((colId) => {
      let label = additionalColumnLabels[colId];
      
      // Format custom column labels (remove 'custom_' prefix and format nicely)
      if (!label && colId.startsWith('custom_')) {
        const fieldName = colId.replace('custom_', '');
        label = fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1');
      }
      
      return {
        id: colId,
        label: label || colId,
        data: getAdditionalColumnData(hearing, colId),
      };
    });

    // IMPROVED: Calculate proper row height with accurate line counting
    let maxLines = 2;

    // Check court name lines
    if (courtName) {
      const courtWidth = colWidths[2] - 16;
      const courtLines = doc.splitTextToSize(courtName, courtWidth);
      maxLines = Math.max(maxLines, courtLines.length);
    }

    // Check CR No lines
    if (crNo) {
      const crWidth = colWidths[1] - 16;
      const crLines = doc.splitTextToSize(crNo, crWidth);
      maxLines = Math.max(maxLines, crLines.length);
    }

    // Check title lines and all content in case title column
    let caseTitleColumnLines = 0;
    if (caseTitle) {
      const titleWidth = colWidths[3] - 16;
      const titleLines = doc.splitTextToSize(caseTitle, titleWidth);
      caseTitleColumnLines += titleLines.length;
    }

    // Add lines for CNR
    if (cnrNo) {
      const cnrWidth = colWidths[3] - 16;
      const cnrLines = doc.splitTextToSize(`CNR: ${cnrNo}`, cnrWidth);
      caseTitleColumnLines += cnrLines.length;
    }

    // --- FIX START: Correctly calculate height for Next Hearing ---
    if (nextHearing) {
      const nextHearingWidth = colWidths[3] - 16;
      const nextHearingLines = doc.splitTextToSize(
        `Next Hearing: ${nextHearing}`,
        nextHearingWidth
      );
      caseTitleColumnLines += nextHearingLines.length;
    }
    // --- FIX END ---

    // Add lines for Coram (COMPULSORY - always displayed)
    if (coramValue) {
      const coramWidth = colWidths[3] - 16;
      const coramLines = doc.splitTextToSize(
        `Coram: ${coramValue}`,
        coramWidth
      );
      caseTitleColumnLines += coramLines.length;
    }

    // IMPROVED: Account for caseStatus and caseDetails in case title column ONLY IN PORTRAIT MODE
    if (isPortraitMode) {
      // Add gap line if there's any content before additional sections
      if (caseTitleColumnLines > 0) {
        caseTitleColumnLines += 0.7; // Small gap (5pt)
      }

      // Add lines for case status - MORE READABLE SPACING
      if (
        additionalColumns.includes("caseStatus") &&
        caseStatusData &&
        caseStatusData.type === "caseStatus"
      ) {
        const hasAnyStatus =
          caseStatusData.stageOfCase ||
          caseStatusData.benchType ||
          caseStatusData.judicialBranch;

        if (hasAnyStatus) {
          caseTitleColumnLines += 1.2; // Section label (10pt spacing)

          if (caseStatusData.stageOfCase) {
            caseTitleColumnLines += 1.1; // Label (9pt spacing)
            const stageLines = doc.splitTextToSize(
              caseStatusData.stageOfCase,
              colWidths[3] - 20
            );
            caseTitleColumnLines += stageLines.length * 0.9;
          }
          if (caseStatusData.benchType) {
            caseTitleColumnLines += 1.1; // Label (9pt spacing)
            caseTitleColumnLines += 1.1; // Value (9pt spacing)
          }
          if (caseStatusData.judicialBranch) {
            caseTitleColumnLines += 1.1; // Label (9pt spacing)
            caseTitleColumnLines += 1.1; // Value (9pt spacing)
          }
          caseTitleColumnLines += 0.7; // Section spacing (5pt)
        }
      }

      if (
        additionalColumns.includes("caseDetails") && // THIS CHECK IS CRITICAL
        caseDetailsData &&
        caseDetailsData.type === "caseDetails"
      ) {
        const hasAnyDetail =
          caseDetailsData.filingDate ||
          caseDetailsData.regDate ||
          caseDetailsData.regNumber;

        if (hasAnyDetail) {
          caseTitleColumnLines += 1.2; // Section label (10pt spacing)

          if (caseDetailsData.filingDate) {
            caseTitleColumnLines += 1.1; // Label (9pt spacing)
            caseTitleColumnLines += 1.1; // Value (9pt spacing)
          }
          if (caseDetailsData.regDate) {
            caseTitleColumnLines += 1.1; // Label (9pt spacing)
            caseTitleColumnLines += 1.1; // Value (9pt spacing)
          }
          if (caseDetailsData.regNumber) {
            caseTitleColumnLines += 1.1; // Label (9pt spacing)
            const regLines = doc.splitTextToSize(
              caseDetailsData.regNumber,
              colWidths[3] - 20
            );
            caseTitleColumnLines += regLines.length * 0.9; // Values (9pt each)
          }
        }
      }
    }
    maxLines = Math.max(maxLines, caseTitleColumnLines);

    // Check regular columns (landscape) - FILTER OUT caseStatus and caseDetails ONLY IN PORTRAIT MODE
    let hasAdditionalContent = false;
    regularColumnsData.forEach((data, idx) => {
      const columnId = columnsToShow[idx];
      // Skip caseStatus and caseDetails ONLY in portrait mode (they show as separate columns in landscape)
      const shouldSkip =
        isPortraitMode &&
        (columnId === "caseStatus" || columnId === "caseDetails");

      if (!shouldSkip) {
        // Check if this column has actual data
        const hasData =
          data &&
          ((typeof data === "string" && data !== "N/A") ||
            (typeof data === "object" &&
              data.type &&
              ((data.type === "advocates" && data.value) ||
                (data.type === "coram" && data.value) ||
                (data.type === "categoryDetails" && data.category) ||
                (data.type === "actsData" &&
                  data.acts &&
                  data.acts.length > 0))));

        if (hasData) {
          hasAdditionalContent = true;
          const colWidth = colWidths[4 + idx] - 16;
          let lineCount = calculateLineCount(data, colWidth);
          maxLines = Math.max(maxLines, lineCount);
        }
      }
    });

    // Check stacked columns (portrait) - FILTER OUT caseStatus and caseDetails ONLY IN PORTRAIT MODE
    if (isPortraitMode && stackedColumnsData.length > 0) {
      const stackedColWidth = colWidths[4] - 16;
      let totalStackedLines = 0;
      let hasStackedContent = false;

      stackedColumnsData.forEach((colInfo) => {
        // Skip caseStatus and caseDetails ONLY in portrait mode (they're in case title column)
        const shouldSkip =
          isPortraitMode &&
          (colInfo.id === "caseStatus" || colInfo.id === "caseDetails");

        if (!shouldSkip) {
          const data = colInfo.data;
          // Check if this column has actual data
          const hasData =
            data &&
            ((typeof data === "string" && data !== "N/A") ||
              (typeof data === "object" &&
                data.type &&
                ((data.type === "advocates" && data.value) ||
                  (data.type === "coram" && data.value) ||
                  (data.type === "categoryDetails" && data.category) ||
                  (data.type === "actsData" &&
                    data.acts &&
                    data.acts.length > 0))));

          if (hasData) {
            hasStackedContent = true;
            // Add 1 line for label (10pt spacing)
            totalStackedLines += 1;
            // Add lines for data
            const dataLines = calculateLineCount(colInfo.data, stackedColWidth);
            totalStackedLines += dataLines;
            // Add spacing between sections (5pt = 0.417 lines)
            totalStackedLines += 0.417;
          }
        }
      });

      if (hasStackedContent && totalStackedLines > 0) {
        maxLines = Math.max(maxLines, totalStackedLines);
      }
    }

    // Calculate dynamic line height based on content
    let lineHeight;
    if (isPortraitMode) {
      const hasCaseStatus =
        additionalColumns.includes("caseStatus") &&
        caseStatusData &&
        caseStatusData.type === "caseStatus" &&
        (caseStatusData.stageOfCase ||
          caseStatusData.benchType ||
          caseStatusData.judicialBranch);

      const hasCaseDetails =
        additionalColumns.includes("caseDetails") &&
        caseDetailsData &&
        caseDetailsData.type === "caseDetails" &&
        (caseDetailsData.filingDate ||
          caseDetailsData.regDate ||
          caseDetailsData.regNumber);

      // Both present = 10, only one or none = 12
      lineHeight = hasCaseStatus && hasCaseDetails ? 10 : 12;
    } else {
      lineHeight = 12;
    }

    const topPadding = 15;
    const bottomPadding = 0;
    const rowHeight = maxLines * lineHeight + topPadding + bottomPadding;

    // IMPROVED: Check if we need a new page
    if (currentY + rowHeight > pageHeight - 80) {
      addFooter();
      doc.addPage();
      pageNumber++;
      currentY = margin + 20;
      drawTableHeader();
    }

    doc.setFillColor(...colors.white);
    doc.rect(margin, currentY, contentWidth, rowHeight, "F");

    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);
    doc.rect(margin, currentY, contentWidth, rowHeight, "S");

    // Vertical lines
    let xPos = margin + colWidths[0];
    for (let i = 0; i < colWidths.length - 1; i++) {
      doc.line(xPos, currentY, xPos, currentY + rowHeight);
      xPos += colWidths[i + 1];
    }

    let textY = currentY + topPadding;

    // SL No
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.black);
    doc.text(slNo, margin + 25, textY);

    // CR No - IMPROVED: Show all lines
    const crLines = doc.splitTextToSize(crNo, colWidths[1] - 16);
    let crY = textY;
    crLines.forEach((line) => {
      if (crY < currentY + rowHeight - bottomPadding) {
        doc.text(line, margin + colWidths[0] + 8, crY);
        crY += 12;
      }
    });

    // Court Name - IMPROVED: Show all lines
    const courtLines = doc.splitTextToSize(courtName, colWidths[2] - 16);
    let courtY = textY;
    courtLines.forEach((line) => {
      if (courtY < currentY + rowHeight - bottomPadding) {
        doc.text(line, margin + colWidths[0] + colWidths[1] + 8, courtY);
        courtY += 12;
      }
    });

    // Case Title - IMPROVED: Show all lines
    const caseTitleX = margin + colWidths[0] + colWidths[1] + colWidths[2] + 8;
    let caseY = textY;

    if (caseTitle) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...colors.black);
      const titleLines = doc.splitTextToSize(caseTitle, colWidths[3] - 16);
      titleLines.forEach((line) => {
        if (caseY < currentY + rowHeight - bottomPadding) {
          doc.text(line, caseTitleX, caseY);
          caseY += 12;
        }
      });
      caseY += 5;
    }

    // CNR - IMPROVED: Show all lines
    if (cnrNo) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...colors.gray);
      const cnrLines = doc.splitTextToSize(`CNR: ${cnrNo}`, colWidths[3] - 16);
      cnrLines.forEach((line) => {
        if (caseY < currentY + rowHeight - bottomPadding) {
          doc.text(line, caseTitleX, caseY);
          caseY += 9;
        }
      });
      caseY += 3;
    }

    // --- FIX START: Wrap Next Hearing text correctly ---
    if (nextHearing && caseY < currentY + rowHeight - bottomPadding) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      // Apply green color if from customUndatedRows, otherwise blue
      if (isCustomNextHearing) {
        doc.setTextColor(5, 150, 105); // #059669 in RGB
      } else {
        doc.setTextColor(...colors.blue);
      }

      const nextHearingWidth = colWidths[3] - 16;
      const nextHearingLines = doc.splitTextToSize(
        `Next Hearing: ${nextHearing}`,
        nextHearingWidth
      );

      nextHearingLines.forEach((line) => {
        if (caseY < currentY + rowHeight - bottomPadding) {
          doc.text(line, caseTitleX, caseY);
          caseY += 10;
        }
      });
      caseY += 3;
    }
    // --- FIX END ---

    // Coram (COMPULSORY - always displayed)
    if (coramValue && caseY < currentY + rowHeight - bottomPadding) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...colors.red);
      const coramLines = doc.splitTextToSize(
        `Coram: ${coramValue}`,
        colWidths[3] - 16
      );
      coramLines.forEach((line) => {
        if (caseY < currentY + rowHeight - bottomPadding) {
          doc.text(line, caseTitleX, caseY);
          caseY += 10;
        }
      });
      caseY += 3;
    }

    // PORTRAIT MODE ONLY: Add Case Status and Case Details under Case Title
    if (isPortraitMode) {
      // Add a small gap before additional sections
      if (caseY > textY) {
        caseY += 5;
      }

      // Add Case Status under Case Title - MORE READABLE
      if (
        additionalColumns.includes("caseStatus") &&
        caseStatusData &&
        caseStatusData.type === "caseStatus" &&
        caseY < currentY + rowHeight - bottomPadding
      ) {
        const hasAnyStatus =
          caseStatusData.stageOfCase ||
          caseStatusData.benchType ||
          caseStatusData.judicialBranch;

        if (hasAnyStatus) {
          // Section Label (readable size)
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);
          doc.setTextColor(...colors.darkBlue);
          doc.text("Case Status:", caseTitleX, caseY);
          caseY += 10;

          // Stage of Case
          if (
            caseStatusData.stageOfCase &&
            caseY < currentY + rowHeight - bottomPadding
          ) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Stage of Case:", caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...colors.blue);
            const stageLines = doc.splitTextToSize(
              caseStatusData.stageOfCase,
              colWidths[3] - 20
            );

            stageLines.forEach((line) => {
              if (caseY < currentY + rowHeight - bottomPadding) {
                doc.text(line, caseTitleX + 3, caseY);
                caseY += 9;
              }
            });
            caseY += 2;
          }

          // Bench Type
          if (
            caseStatusData.benchType &&
            caseY < currentY + rowHeight - bottomPadding
          ) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Bench Type:", caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...colors.purple);
            doc.text(caseStatusData.benchType, caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 2;
          }

          // Judicial Branch
          if (
            caseStatusData.judicialBranch &&
            caseY < currentY + rowHeight - bottomPadding
          ) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Judicial Branch:", caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...colors.teal);
            doc.text(caseStatusData.judicialBranch, caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 2;
          }

          // Add spacing after this section
          caseY += 5;
        }
      }

      // Add Case Details under Case Title - MORE READABLE
      if (
        additionalColumns.includes("caseDetails") &&
        caseDetailsData &&
        caseDetailsData.type === "caseDetails" &&
        caseY < currentY + rowHeight - bottomPadding
      ) {
        const hasAnyDetail =
          caseDetailsData.filingDate ||
          caseDetailsData.regDate ||
          caseDetailsData.regNumber;

        if (hasAnyDetail) {
          // Section Label (readable size)
          doc.setFont("helvetica", "bold");
          doc.setFontSize(7.5);
          doc.setTextColor(...colors.darkBlue);
          doc.text("Case Details:", caseTitleX, caseY);
          caseY += 10;

          // Filing Date
          if (
            caseDetailsData.filingDate &&
            caseY < currentY + rowHeight - bottomPadding
          ) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Filing Date:", caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(41, 128, 185);
            doc.text(caseDetailsData.filingDate, caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 2;
          }

          // Registration Date
          if (
            caseDetailsData.regDate &&
            caseY < currentY + rowHeight - bottomPadding
          ) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Reg. Date:", caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...colors.purple);
            doc.text(caseDetailsData.regDate, caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 2;
          }

          // Registration Number
          if (
            caseDetailsData.regNumber &&
            caseY < currentY + rowHeight - bottomPadding
          ) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...colors.gray);
            doc.text("Reg. Number:", caseTitleX + 3, caseY);
            caseY += 9;
            caseY += 1;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...colors.green);
            const regLines = doc.splitTextToSize(
              caseDetailsData.regNumber,
              colWidths[3] - 20
            );
            const isSupremeCourt = courtName.toLowerCase().includes("supreme court");
            regLines.forEach((line) => {
              if (caseY < currentY + rowHeight - bottomPadding) {
                doc.text(line, caseTitleX + 3, caseY);
                if (isSupremeCourt) caseY += 9;
              }
            });
            if (isSupremeCourt) caseY += 2;
          }
        }
      }
    }
    // END PORTRAIT MODE ONLY SECTION

    // Additional data handling - FILTER OUT caseStatus and caseDetails ONLY IN PORTRAIT MODE
    const additionalX =
      margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + 8;
    let additionalY = textY;

    if (isPortraitMode && stackedColumnsData.length > 0) {
      // PORTRAIT MODE: Stack all additional columns in the 5th column (except caseStatus and caseDetails)
      const stackedColWidth = colWidths[4] - 16;

      stackedColumnsData.forEach((colInfo, colIdx) => {
        // Skip caseStatus, caseDetails, and coram (they're in case title column)
        const shouldSkip =
          colInfo.id === "caseStatus" ||
          colInfo.id === "caseDetails" ||
          colInfo.id === "coram";

        if (!shouldSkip) {
          if (additionalY < currentY + rowHeight - bottomPadding) {
            // Render label
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7);
            doc.setTextColor(...colors.darkBlue);
            doc.text(colInfo.label + ":", additionalX, additionalY);
            additionalY += 10;

            // Render data
            additionalY = renderAdditionalColumnData(
              colInfo.data,
              colInfo.id,
              additionalX,
              additionalY,
              stackedColWidth,
              currentY + rowHeight - bottomPadding
            );

            // Add spacing before next section
            additionalY += 5;
          }
        }
      });
    } else {
      // LANDSCAPE MODE: Render regular columns side by side, then stacked custom columns in Additional column
      let landscapeX = additionalX;

      // Render regular additional columns
      regularColumnsData.forEach((data, idx) => {
        const columnId = columnsToShow[idx];
        // Skip coram in landscape mode too (it's already in case title column)
        if (columnId === "coram") {
          return; // Skip this column
        }
        const colWidth = colWidths[4 + idx];
        let landscapeY = textY;

        landscapeY = renderAdditionalColumnData(
          data,
          columnsToShow[idx],
          landscapeX,
          landscapeY,
          colWidth - 16,
          currentY + rowHeight - bottomPadding
        );

        landscapeX += colWidth;
      });
      
      // Render stacked custom columns in "Additional" column in landscape mode
      if (stackedColumnsData.length > 0) {
        const stackedColWidth = colWidths[colWidths.length - 1] - 16;
        let stackedY = textY;

        stackedColumnsData.forEach((colInfo, colIdx) => {
          // In landscape mode, only show custom columns in Additional column
          if (colInfo.id.startsWith('custom_')) {
            if (stackedY < currentY + rowHeight - bottomPadding) {
              // Render label
              doc.setFont("helvetica", "bold");
              doc.setFontSize(7);
              doc.setTextColor(...colors.darkBlue);
              doc.text(colInfo.label + ":", landscapeX, stackedY);
              stackedY += 10;

              // Render data
              stackedY = renderAdditionalColumnData(
                colInfo.data,
                colInfo.id,
                landscapeX,
                stackedY,
                stackedColWidth,
                currentY + rowHeight - bottomPadding
              );

              // Add spacing before next section
              stackedY += 5;
            }
          }
        });
      }
    }

    currentY += rowHeight;
  };

  const addTasksTable = async (tasksList) => {
    currentY += 12;
    addCleanSectionHeader(
      `Tasks & Internal Matters (${tasksList.length})`,
      true
    );

    const headerHeight = 28;
    doc.setFillColor(...colors.lightGray);
    doc.rect(margin, currentY, contentWidth, headerHeight, "F");

    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);
    doc.rect(margin, currentY, contentWidth, headerHeight, "S");

    const colWidths = [130, 90, 80, 200];

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.black);

    let xPos = margin + 8;
    doc.text("CNR No", xPos, currentY + 18);
    xPos += colWidths[0];
    doc.text("Created At", xPos, currentY + 18);
    xPos += colWidths[1];
    doc.text("Status", xPos, currentY + 18);
    xPos += colWidths[2];
    doc.text("Task Details", xPos, currentY + 18);

    xPos = margin + colWidths[0];
    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);
    for (let i = 0; i < 3; i++) {
      doc.line(xPos, currentY, xPos, currentY + headerHeight);
      xPos += colWidths[i + 1];
    }

    currentY += headerHeight;

    for (let i = 0; i < tasksList.length; i++) {
      await addTaskRow(tasksList[i], colWidths);
    }
  };

  const addTaskRow = async (task, colWidths) => {
    const cnrNo = task.courtRef || "INTERNAL MATTER";
    const createdAt = formatDate(task.createdAt);
    const status = task.isCompleted ? "COMPLETED" : "PENDING";
    const taskName = task.taskName || "";

    const assignedToNames =
      task.assignedTo && Array.isArray(task.assignedTo)
        ? await Promise.all(task.assignedTo.map((id) => fetchUserName(id)))
        : ["Unassigned"];

    const dueDate = formatDate(task.dueDate);

    let estimatedLines = 2;
    if (taskName) {
      const taskWidth = colWidths[3] - 16;
      const taskLines = doc.splitTextToSize(taskName, taskWidth);
      estimatedLines = Math.max(estimatedLines, taskLines.length);
    }

    const topPadding = 15;
    const bottomPadding = 15;
    const rowHeight = estimatedLines * 12 + topPadding + bottomPadding;

    if (currentY + rowHeight > pageHeight - 80) {
      addFooter();
      doc.addPage();
      pageNumber++;
      currentY = margin + 20;
    }

    doc.setFillColor(...colors.white);
    doc.rect(margin, currentY, contentWidth, rowHeight, "F");

    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);
    doc.rect(margin, currentY, contentWidth, rowHeight, "S");

    let xPos = margin + colWidths[0];
    for (let i = 0; i < 3; i++) {
      doc.line(xPos, currentY, xPos, currentY + rowHeight);
      xPos += colWidths[i + 1];
    }

    let textY = currentY + topPadding;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.black);
    const cnrLines = doc.splitTextToSize(cnrNo, colWidths[0] - 16);
    let cnrY = textY;
    cnrLines.slice(0, 2).forEach((line) => {
      doc.text(line, margin + 8, cnrY);
      cnrY += 11;
    });

    doc.text(createdAt, margin + colWidths[0] + 8, textY);

    const statusX = margin + colWidths[0] + colWidths[1] + 10;
    const statusY = currentY + 10;

    if (task.isCompleted) {
      doc.setFillColor(...colors.green);
      doc.setTextColor(...colors.white);
    } else {
      doc.setFillColor(...colors.pendingBg);
      doc.setTextColor(...colors.pendingText);
    }

    doc.roundedRect(statusX, statusY, 60, 16, 3, 3, "F");

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(status, statusX + 30, statusY + 11, { align: "center" });

    const taskDetailsX =
      margin + colWidths[0] + colWidths[1] + colWidths[2] + 8;
    let taskY = textY;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...colors.black);
    const taskLines = doc.splitTextToSize(taskName, colWidths[3] - 16);
    taskLines.slice(0, 2).forEach((line) => {
      doc.text(line, taskDetailsX, taskY);
      taskY += 11;
    });
    taskY += 3;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...colors.gray);
    const assignedText = `Assigned to: ${assignedToNames[0]}`;
    const assignedLines = doc.splitTextToSize(assignedText, colWidths[3] - 16);
    assignedLines.slice(0, 1).forEach((line) => {
      doc.text(line, taskDetailsX, taskY);
      taskY += 13;
    });

    if (!task.isCompleted) {
      doc.setTextColor(...colors.red);
    } else {
      doc.setTextColor(...colors.blue);
    }
    doc.text(`Due: ${dueDate}`, taskDetailsX, taskY);

    currentY += rowHeight;
  };

  const addFooter = () => {
    const footerY = pageHeight - 50;

    // Timestamp on the left
    const now = new Date();
    const timestamp = now.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.gray);
    doc.text(`${timestamp}`, margin, footerY);

    // Page number on the right
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.gray);
    doc.text(`Page ${pageNumber}`, pageWidth - margin, footerY, {
      align: "right",
    });

    // doc.setFontSize(9);
    // doc.setFont("helvetica", "normal");
    // doc.setTextColor(...colors.gray);
    // doc.text("Contact: support@levons.in", pageWidth / 2, footerY, {
    //   align: "center",
    // });
  };

  try {
    let hearingsList = [];
    let tasksList = [];
    let scheduleDate = "";
    let groupedHearingsData = {}; // NEW: Store hearings grouped by date

    if (Array.isArray(dashboardData)) {
      hearingsList = dashboardData;
    } else if (
      dashboardData.hearings &&
      Array.isArray(dashboardData.hearings)
    ) {
      hearingsList = dashboardData.hearings;
    } else if (typeof dashboardData === "object") {
      const datesWithData = Object.keys(dashboardData)
        .filter((date) => {
          const data = dashboardData[date];
          return data && (data.hearings || data.tasks || Array.isArray(data));
        })
        .sort((a, b) => new Date(a) - new Date(b));

      for (const date of datesWithData) {
        const data = dashboardData[date];

        if (data.hearings && Array.isArray(data.hearings)) {
          hearingsList = hearingsList.concat(data.hearings);
          // Store grouped data
          if (data.hearings.length > 0) {
            groupedHearingsData[date] = data.hearings;
          }
        } else if (Array.isArray(data)) {
          hearingsList = hearingsList.concat(data);
          // Store grouped data
          if (data.length > 0) {
            groupedHearingsData[date] = data;
          }
        }

        if (data.tasks && Array.isArray(data.tasks)) {
          tasksList = tasksList.concat(data.tasks);
        }
      }

      // const firstDateKey = Object.keys(dashboardData)[0];
      // if (firstDateKey) {
      //   scheduleDate = formatLongDate(firstDateKey);
      // }
    }
    if (!scheduleDate && hearingsList.length > 0) {
      const firstHearing = hearingsList[0];
      const caseData = firstHearing.caseDate || firstHearing;
      if (caseData.nextHearingDate) {
        scheduleDate = formatLongDate(caseData.nextHearingDate);
      }
    }

    if (!scheduleDate) {
      // PRIORITY 1: Use exportDateRange if provided (THIS SHOULD BE CHECKED FIRST)
      if (exportDateRange && exportDateRange.start && exportDateRange.end) {
        const todayStr = new Date().toISOString().split("T")[0];

        if (exportDateRange.start === exportDateRange.end) {
          // Single date
          if (exportDateRange.start === todayStr) {
            scheduleDate = formatLongDate(new Date());
          } else {
            scheduleDate = formatLongDate(exportDateRange.start);
          }
        } else {
          // Date range
          scheduleDate = `${formatLongDate(
            exportDateRange.start
          )} to ${formatLongDate(exportDateRange.end)}`;
        }
      }
      // PRIORITY 2: Fallback - Check if we have a date range in data
      else {
        const datesInData = Object.keys(dashboardData)
          .filter((date) => {
            const dayData = dashboardData[date];
            return (
              dayData &&
              (dayData.hearings?.length > 0 || dayData.tasks?.length > 0)
            );
          })
          .sort();

        if (datesInData.length > 0) {
          const startDate = datesInData[0];
          const endDate = datesInData[datesInData.length - 1];
          const todayStr = new Date().toISOString().split("T")[0];

          if (startDate === endDate) {
            // Single date - check if it's today
            if (startDate === todayStr) {
              scheduleDate = formatLongDate(new Date());
            } else {
              scheduleDate = formatLongDate(startDate);
            }
          } else {
            // Date range
            scheduleDate = `${formatLongDate(startDate)} to ${formatLongDate(
              endDate
            )}`;
          }
        } else {
          // PRIORITY 3: Final fallback
          scheduleDate = formatLongDate(new Date());
        }
      }
    }

    const companyName = getCompanyName();
    currentY = drawCommonPdfHeader(doc, companyName, pageWidth, margin, colors);

    // Add modern stats section
    // currentY += 15;

    const statsHeight = 75;
    const statsY = currentY;

    // Gradient-like background with border
    doc.setFillColor(245, 247, 250);
    doc.rect(margin, statsY, contentWidth, statsHeight, "F");

    doc.setDrawColor(200, 210, 220);
    doc.setLineWidth(1);
    doc.rect(margin, statsY, contentWidth, statsHeight, "S");

    // Top accent line
    doc.setDrawColor(...colors.blue);
    doc.setLineWidth(3);
    doc.line(margin, statsY, margin + contentWidth, statsY);

    currentY += 24;

    // Main title
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.main);
    const totalItems = hearingsList.length + tasksList.length;
    doc.text(`Dashboard Overview`, pageWidth / 2, currentY, {
      align: "center",
    });

    currentY += 24;

    // Stats row with icons
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 70, 80);

    const statsText = `${totalItems} Total Items  •  ${hearingsList.length} Hearings  •  ${tasksList.length} Tasks`;
    doc.text(statsText, pageWidth / 2, currentY, { align: "center" });

    currentY += 16;

    // Date with subtle styling
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.gray);
    doc.text(scheduleDate, pageWidth / 2, currentY, { align: "center" });

    currentY += 22;

    if (hearingsList.length > 0) {
      // Pass grouped data for date-based grouping
      await addHearingsTable(hearingsList, groupedHearingsData);
    }

    if (tasksList.length > 0) {
      await addTasksTable(tasksList);
    }

    if (hearingsList.length === 0 && tasksList.length === 0) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...colors.gray);
      doc.text(
        "No hearings or tasks scheduled for the selected date(s).",
        pageWidth / 2,
        currentY + 40,
        { align: "center" }
      );
    }

    addFooter();

    // Determine filename date
    let filenameDate = new Date().toISOString().split("T")[0];
    if (exportDateRange && exportDateRange.start && exportDateRange.end) {
      if (exportDateRange.start === exportDateRange.end) {
        filenameDate = exportDateRange.start;
      } else {
        filenameDate = `${exportDateRange.start}_to_${exportDateRange.end}`;
      }
    }
    const filename = `LawSuit_Pro_Dashboard_${filenameDate}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};
